import React, { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Save, Plus, FileEdit, Trash2, GripVertical } from "lucide-react";
import { db } from "@/lib/firebase";
import { doc, getDoc, collection, getDocs, addDoc, updateDoc, deleteDoc, setDoc } from "firebase/firestore";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const defaultAbout = {
  title: "About MSS Driving School",
  description:
    "With over 15 years of experience, MSS Driving School has been the leading driving school in the region. Our mission is to create safe, confident, and responsible drivers through comprehensive training and personalized instruction.",
  features: [
    {
      title: "Experienced Instructors",
      description:
        "Our instructors have years of professional driving experience and are certified to teach defensive driving techniques.",
    },
    {
      title: "Modern Fleet",
      description:
        "Learn on well-maintained, modern vehicles equipped with dual controls for safety during your training sessions.",
    },
    {
      title: "Flexible Scheduling",
      description:
        "We offer flexible class schedules to accommodate your busy lifestyle, with weekend and evening options available.",
    },
  ],
  stats: {
    graduates: "5,000+",
    experience: "15+",
    vehicles: "20+",
    passRate: "98%",
  },
};
const defaultContact = {
  phone: "(123) 456-7890",
  email: "info@drivemaster.com",
  address: "123 Driving Avenue, City, State 12345",
};

// Sortable Course Item Component
const SortableItem = ({ id, course, branches, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between border rounded p-3 ${
        isDragging ? 'bg-gray-50 shadow-lg' : 'bg-white'
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </div>
        <div>
          <div className="font-medium">{course.title}</div>
          <div className="text-sm text-muted-foreground">
            {course.category} • ₱{course.price?.toLocaleString?.() ?? course.price}
          </div>
          <div className="text-xs text-muted-foreground">{course.duration}</div>
          <div className="text-xs text-muted-foreground">
            Branches: {course.branches && course.branches.length > 0 
              ? course.branches.map(branchId => {
                  const branch = branches.find(b => b.id === branchId);
                  return branch ? branch.name : branchId;
                }).join(", ")
              : "All branches"}
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => onEdit(course)}>
          <FileEdit className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="destructive" onClick={() => onDelete(course.id)}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const Content: React.FC = () => {
  const [activeContentTab, setActiveContentTab] = useState("about");
  const [aboutContent, setAboutContent] = useState<any>(defaultAbout);
  const [contactInfo, setContactInfo] = useState<any>(defaultContact);
  const [courses, setCourses] = useState<any[] | null>(null);
  const [branches, setBranches] = useState<any[]>([]);
  const [newCourse, setNewCourse] = useState({
    title: "",
    description: "",
    price: 0,
    duration: "",
    totalHours: 0,
    category: "beginner",
    featured: false,
    branches: [] as string[],
    displayOrder: 0,
  });
  const [newBranch, setNewBranch] = useState({ name: "", address: "", contact: "", email: "", social: "" });
  const [editingBranch, setEditingBranch] = useState<any | null>(null);
  const [editingCourse, setEditingCourse] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Branch filter for course ordering
  const [selectedBranchFilter, setSelectedBranchFilter] = useState<string>("all");

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const filteredCourses = getFilteredCourses();
      const oldIndex = filteredCourses.findIndex((course) => course.id === active.id);
      const newIndex = filteredCourses.findIndex((course) => course.id === over.id);

      const newOrder = arrayMove(filteredCourses, oldIndex, newIndex);
      
      // Update the display order for the selected branch
      const branchKey = selectedBranchFilter === "all" ? "displayOrder" : `displayOrder_${selectedBranchFilter}`;
      
      // Update display order in Firebase
      const updatePromises = newOrder.map((course, index) => 
        updateDoc(doc(db, "courses", course.id), { [branchKey]: index })
      );
      
      try {
        await Promise.all(updatePromises);
        
        // Update local state
        const updatedCourses = courses.map(course => {
          const newOrderCourse = newOrder.find(c => c.id === course.id);
          if (newOrderCourse) {
            return { ...course, [branchKey]: newOrder.indexOf(newOrderCourse) };
          }
          return course;
        });
        setCourses(updatedCourses);
      } catch (error) {
        console.error("Error updating course order:", error);
      }
    }
  };

  // Filter courses based on selected branch
  const getFilteredCourses = () => {
    let filtered = courses;
    
    if (selectedBranchFilter !== "all") {
      filtered = courses.filter(course => 
        course.branches && course.branches.includes(selectedBranchFilter)
      );
    }
    
    // Sort by branch-specific display order
    const branchKey = selectedBranchFilter === "all" ? "displayOrder" : `displayOrder_${selectedBranchFilter}`;
    return filtered.sort((a, b) => {
      const aOrder = (a as any)[branchKey] ?? 999999;
      const bOrder = (b as any)[branchKey] ?? 999999;
      return aOrder - bOrder;
    });
  };

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      setError(null);
      try {
        const aboutSnap = await getDoc(doc(db, "content", "about"));
        if (aboutSnap.exists()) setAboutContent(aboutSnap.data());
        const contactSnap = await getDoc(doc(db, "content", "contact"));
        if (contactSnap.exists()) setContactInfo(contactSnap.data());
        const coursesSnap = await getDocs(collection(db, "courses"));
        const fetchedCourses = coursesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })) as any[];
        
        // Sort courses by displayOrder, putting courses without displayOrder at the end
        const sortedCourses = fetchedCourses.sort((a, b) => {
          const aOrder = a.displayOrder ?? 999999;
          const bOrder = b.displayOrder ?? 999999;
          return aOrder - bOrder;
        });
        
        setCourses(sortedCourses);
        // Fetch branches
        const branchesSnap = await getDocs(collection(db, "branches"));
        setBranches(branchesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      } catch (err: any) {
        setError("Failed to load content.");
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const handleSaveAbout = async () => {
    try {
      await setDoc(doc(db, "content", "about"), aboutContent);
      alert("About content saved successfully!");
    } catch (err) {
      alert("Failed to save about content.");
    }
  };

  const handleSaveContact = () => {
    alert("Contact information saved successfully!");
  };

  const handleSaveCourse = (course) => {
    if (editingCourse) {
      setCourses(courses.map((c) => (c.id === course.id ? course : c)));
      setEditingCourse(null);
    } else {
      const newId = (
        Math.max(...courses.map((c) => parseInt(c.id))) + 1
      ).toString();
      setCourses([...courses, { ...course, id: newId }]);
      setNewCourse({
        title: "",
        description: "",
        price: 0,
        duration: "",
        totalHours: 0,
        category: "beginner",
        featured: false,
        branches: [],
        displayOrder: 0,
      });
    }
    alert("Course saved successfully!");
  };

  const handleDeleteCourse = (courseId) => {
    if (confirm("Are you sure you want to delete this course?")) {
      setCourses(courses.filter((c) => c.id !== courseId));
      alert("Course deleted successfully!");
    }
  };

  const handleSaveBranch = async () => {
    if (!newBranch.name.trim() || !newBranch.address.trim()) {
      alert("Branch name and address are required.");
      return;
    }
    try {
      const docRef = await addDoc(collection(db, "branches"), newBranch);
      setBranches([...branches, { ...newBranch, id: docRef.id }]);
      setNewBranch({ name: "", address: "", contact: "", email: "", social: "" });
      alert("Branch added successfully!");
    } catch (err) {
      alert("Failed to add branch.");
    }
  };

  const handleEditBranch = (branch) => setEditingBranch(branch);
  const handleUpdateBranch = async () => {
    try {
      await updateDoc(doc(db, "branches", editingBranch.id), {
        name: editingBranch.name,
        address: editingBranch.address,
        contact: editingBranch.contact,
        email: editingBranch.email,
        social: editingBranch.social,
      });
      setBranches(branches.map((b) => (b.id === editingBranch.id ? editingBranch : b)));
      setEditingBranch(null);
      alert("Branch updated successfully!");
    } catch (err) {
      alert("Failed to update branch.");
    }
  };
  const handleDeleteBranch = async (id) => {
    try {
      await deleteDoc(doc(db, "branches", id));
      setBranches(branches.filter((b) => b.id !== id));
      alert("Branch deleted successfully!");
    } catch (err) {
      alert("Failed to delete branch.");
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Content Management</h2>
      {loading ? (
        <div className="py-8 text-center text-muted-foreground">Loading...</div>
      ) : error ? (
        <div className="py-8 text-center text-destructive">{error}</div>
      ) : (
        <Tabs value={activeContentTab} onValueChange={setActiveContentTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="about">About Page</TabsTrigger>
            <TabsTrigger value="courses">Courses</TabsTrigger>
            <TabsTrigger value="branches">Branches</TabsTrigger>
          </TabsList>
          <TabsContent value="about" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>About Page Content</CardTitle>
                <CardDescription>Edit the about section content</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="about-title">Title</Label>
                  <Input
                    id="about-title"
                    value={aboutContent.title}
                    onChange={(e) =>
                      setAboutContent({ ...aboutContent, title: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="about-description">Description</Label>
                  <Textarea
                    id="about-description"
                    value={aboutContent.description}
                    onChange={(e) =>
                      setAboutContent({ ...aboutContent, description: e.target.value })
                    }
                    rows={4}
                  />
                </div>
                <div className="space-y-4">
                  <Label>Features</Label>
                  {aboutContent.features.map((feature, index) => (
                    <Card key={index} className="p-4">
                      <div className="space-y-2">
                        <Input
                          placeholder="Feature title"
                          value={feature.title}
                          onChange={(e) => {
                            const newFeatures = [...aboutContent.features];
                            newFeatures[index].title = e.target.value;
                            setAboutContent({
                              ...aboutContent,
                              features: newFeatures,
                            });
                          }}
                        />
                        <Textarea
                          placeholder="Feature description"
                          value={feature.description}
                          onChange={(e) => {
                            const newFeatures = [...aboutContent.features];
                            newFeatures[index].description = e.target.value;
                            setAboutContent({
                              ...aboutContent,
                              features: newFeatures,
                            });
                          }}
                          rows={2}
                        />
                      </div>
                    </Card>
                  ))}
                </div>
                <div className="space-y-4">
                  <Label>Statistics</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="graduates">Graduates</Label>
                      <Input
                        id="graduates"
                        value={aboutContent.stats.graduates}
                        onChange={(e) =>
                          setAboutContent({
                            ...aboutContent,
                            stats: {
                              ...aboutContent.stats,
                              graduates: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="experience">Years Experience</Label>
                      <Input
                        id="experience"
                        value={aboutContent.stats.experience}
                        onChange={(e) =>
                          setAboutContent({
                            ...aboutContent,
                            stats: {
                              ...aboutContent.stats,
                              experience: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vehicles">Vehicles</Label>
                      <Input
                        id="vehicles"
                        value={aboutContent.stats.vehicles}
                        onChange={(e) =>
                          setAboutContent({
                            ...aboutContent,
                            stats: {
                              ...aboutContent.stats,
                              vehicles: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="passRate">Pass Rate</Label>
                      <Input
                        id="passRate"
                        value={aboutContent.stats.passRate}
                        onChange={(e) =>
                          setAboutContent({
                            ...aboutContent,
                            stats: {
                              ...aboutContent.stats,
                              passRate: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button onClick={handleSaveAbout}>
                  <Save className="h-4 w-4 mr-2" />
                  Save About Content
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          <TabsContent value="courses" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Courses</CardTitle>
                <CardDescription>Manage all driving courses</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="course-title">Course Title</Label>
                    <Input
                      id="course-title"
                      value={editingCourse ? editingCourse.title : newCourse.title}
                      onChange={e => editingCourse ? setEditingCourse({ ...editingCourse, title: e.target.value }) : setNewCourse({ ...newCourse, title: e.target.value })}
                      placeholder="e.g. Basic Driving Course"
                    />
                  </div>
                  <div>
                    <Label htmlFor="course-category">Category</Label>
                    <select
                      id="course-category"
                      className="w-full border rounded px-3 py-2 bg-background text-foreground"
                      value={editingCourse ? editingCourse.category : newCourse.category}
                      onChange={e => editingCourse ? setEditingCourse({ ...editingCourse, category: e.target.value }) : setNewCourse({ ...newCourse, category: e.target.value })}
                    >
                      <option value="beginner">Beginner</option>
                      <option value="advanced">Advanced</option>
                      <option value="specialized">Specialized</option>
                      <option value="refresher">Refresher</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="course-price">Price (₱)</Label>
                    <Input
                      id="course-price"
                      type="number"
                      value={editingCourse ? editingCourse.price : newCourse.price}
                      onChange={e => editingCourse ? setEditingCourse({ ...editingCourse, price: Number(e.target.value) }) : setNewCourse({ ...newCourse, price: Number(e.target.value) })}
                      placeholder="e.g. 5000"
                    />
                  </div>
                  <div>
                    <Label htmlFor="course-duration">Duration</Label>
                    <Input
                      id="course-duration"
                      value={editingCourse ? editingCourse.duration : newCourse.duration}
                      onChange={e => {
                        const durationValue = e.target.value;
                        // Extract number from duration string (e.g., "8 Hours" -> 8)
                        const hoursMatch = durationValue.match(/(\d+)/);
                        const totalHours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
                        
                        if (editingCourse) {
                          setEditingCourse({ 
                            ...editingCourse, 
                            duration: durationValue,
                            totalHours: totalHours
                          });
                        } else {
                          setNewCourse({ 
                            ...newCourse, 
                            duration: durationValue,
                            totalHours: totalHours
                          });
                        }
                      }}
                      placeholder="e.g. 15 Hours"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="course-description">Description</Label>
                  <Textarea
                    id="course-description"
                    value={editingCourse ? editingCourse.description : newCourse.description}
                    onChange={e => editingCourse ? setEditingCourse({ ...editingCourse, description: e.target.value }) : setNewCourse({ ...newCourse, description: e.target.value })}
                    placeholder="Course description..."
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="course-featured"
                    checked={editingCourse ? editingCourse.featured : newCourse.featured}
                    onChange={e => editingCourse ? setEditingCourse({ ...editingCourse, featured: e.target.checked }) : setNewCourse({ ...newCourse, featured: e.target.checked })}
                  />
                  <Label htmlFor="course-featured">Featured Course</Label>
                </div>
                <div>
                  <Label>Available Branches *</Label>
                  <div className="border rounded p-4 space-y-2 max-h-32 overflow-y-auto">
                    {branches.map(branch => (
                      <div key={branch.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`branch-${branch.id}`}
                          checked={editingCourse ? 
                            (editingCourse.branches || []).includes(branch.id) : 
                            newCourse.branches.includes(branch.id)
                          }
                          onChange={() => {
                            const toggleBranch = (currentBranches: string[]) => {
                              return currentBranches.includes(branch.id)
                                ? currentBranches.filter(id => id !== branch.id)
                                : [...currentBranches, branch.id];
                            };
                            
                            if (editingCourse) {
                              setEditingCourse({ 
                                ...editingCourse, 
                                branches: toggleBranch(editingCourse.branches || [])
                              });
                            } else {
                              setNewCourse({ 
                                ...newCourse, 
                                branches: toggleBranch(newCourse.branches)
                              });
                            }
                          }}
                          className="rounded"
                        />
                        <label htmlFor={`branch-${branch.id}`} className="text-sm cursor-pointer">
                          {branch.name}
                        </label>
                      </div>
                    ))}
                  </div>
                  {((editingCourse && (!editingCourse.branches || editingCourse.branches.length === 0)) || 
                    (!editingCourse && newCourse.branches.length === 0)) && (
                    <p className="text-sm text-red-500 mt-1">Please select at least one branch</p>
                  )}
                </div>
                <div className="flex gap-2">
                  {editingCourse ? (
                    <>
                      <Button onClick={async () => {
                        if (!editingCourse.title || !editingCourse.description || editingCourse.price <= 0 || !editingCourse.branches || editingCourse.branches.length === 0) {
                          alert("Please fill in all required fields and select at least one branch.");
                          return;
                        }
                        await updateDoc(doc(db, "courses", editingCourse.id), editingCourse);
                        setCourses(courses.map((c) => c.id === editingCourse.id ? editingCourse : c));
                        setEditingCourse(null);
                        setNewCourse({ title: "", description: "", price: 0, duration: "", totalHours: 0, category: "beginner", featured: false, branches: [], displayOrder: 0 });
                        alert("Course updated!");
                      }} variant="default">Update Course</Button>
                      <Button onClick={() => setEditingCourse(null)} variant="outline">Cancel</Button>
                    </>
                  ) : (
                    <Button onClick={async () => {
                      if (!newCourse.title || !newCourse.description || newCourse.price <= 0 || newCourse.branches.length === 0) {
                        alert("Please fill in all required fields and select at least one branch.");
                        return;
                      }
                      // Set displayOrder to the next available position
                      const courseWithOrder = { ...newCourse, displayOrder: courses.length };
                      const docRef = await addDoc(collection(db, "courses"), courseWithOrder);
                      setCourses([...courses, { ...courseWithOrder, id: docRef.id }]);
                      setNewCourse({ title: "", description: "", price: 0, duration: "", totalHours: 0, category: "beginner", featured: false, branches: [], displayOrder: 0 });
                      alert("Course added!");
                    }} variant="default">Add Course</Button>
                  )}
                </div>
                <div className="pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-semibold">Course Management</h4>
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">Branch Filter:</label>
                      <select
                        value={selectedBranchFilter}
                        onChange={(e) => setSelectedBranchFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="all">All Branches</option>
                        {branches.map(branch => (
                          <option key={branch.id} value={branch.id}>
                            {branch.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Drag and drop courses to reorder them for the {selectedBranchFilter === "all" ? "homepage" : `${branches.find(b => b.id === selectedBranchFilter)?.name || selectedBranchFilter} branch homepage`}.
                  </p>
                  {(!courses || courses.length === 0) ? (
                    <div className="text-muted-foreground">No courses yet.</div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext items={getFilteredCourses().map(c => c.id)} strategy={verticalListSortingStrategy}>
                        <div className="space-y-2">
                          {getFilteredCourses().map((course) => (
                            <SortableItem
                              key={course.id}
                              id={course.id}
                              course={course}
                              branches={branches}
                              onEdit={setEditingCourse}
                              onDelete={async (courseId) => {
                                if (confirm("Are you sure you want to delete this course?")) {
                                  await deleteDoc(doc(db, "courses", courseId));
                                  setCourses(courses.filter((c) => c.id !== courseId));
                                  alert("Course deleted!");
                                }
                              }}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="branches" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Branches</CardTitle>
                <CardDescription>Manage all driving school branches</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="branch-name">Branch Name</Label>
                    <Input
                      id="branch-name"
                      value={editingBranch ? editingBranch.name : newBranch.name}
                      onChange={e => editingBranch ? setEditingBranch({ ...editingBranch, name: e.target.value }) : setNewBranch({ ...newBranch, name: e.target.value })}
                      placeholder="e.g. Lucena City"
                    />
                  </div>
                  <div>
                    <Label htmlFor="branch-address">Address</Label>
                    <Input
                      id="branch-address"
                      value={editingBranch ? editingBranch.address : newBranch.address}
                      onChange={e => editingBranch ? setEditingBranch({ ...editingBranch, address: e.target.value }) : setNewBranch({ ...newBranch, address: e.target.value })}
                      placeholder="e.g. 123 Main St, Lucena City"
                    />
                  </div>
                  <div>
                    <Label htmlFor="branch-contact">Contact Number</Label>
                    <Input
                      id="branch-contact"
                      value={editingBranch ? editingBranch.contact : newBranch.contact}
                      onChange={e => editingBranch ? setEditingBranch({ ...editingBranch, contact: e.target.value }) : setNewBranch({ ...newBranch, contact: e.target.value })}
                      placeholder="e.g. 0917-123-4567"
                    />
                  </div>
                  <div>
                    <Label htmlFor="branch-email">Email</Label>
                    <Input
                      id="branch-email"
                      value={editingBranch ? editingBranch.email : newBranch.email}
                      onChange={e => editingBranch ? setEditingBranch({ ...editingBranch, email: e.target.value }) : setNewBranch({ ...newBranch, email: e.target.value })}
                      placeholder="e.g. branch@email.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="branch-social">Social Media Link</Label>
                    <Input
                      id="branch-social"
                      value={editingBranch ? editingBranch.social : newBranch.social}
                      onChange={e => editingBranch ? setEditingBranch({ ...editingBranch, social: e.target.value }) : setNewBranch({ ...newBranch, social: e.target.value })}
                      placeholder="e.g. https://facebook.com/msslucena"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  {editingBranch ? (
                    <>
                      <Button onClick={handleUpdateBranch} variant="default">Update Branch</Button>
                      <Button onClick={() => setEditingBranch(null)} variant="outline">Cancel</Button>
                    </>
                  ) : (
                    <Button onClick={handleSaveBranch} variant="default">Add Branch</Button>
                  )}
                </div>
                <div className="pt-6">
                  <h4 className="font-semibold mb-2">All Branches</h4>
                  {branches.length === 0 ? (
                    <div className="text-muted-foreground">No branches yet.</div>
                  ) : (
                    <div className="space-y-2">
                      {branches.map((branch) => (
                        <div key={branch.id} className="flex items-center justify-between border rounded p-3">
                          <div>
                            <div className="font-medium">{branch.name}</div>
                            <div className="text-sm text-muted-foreground">{branch.address}</div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleEditBranch(branch)}><FileEdit className="h-4 w-4" /></Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDeleteBranch(branch.id)}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default Content;
